import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const ServicesCTA = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const glowIntensity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0.05, 0.15, 0.05]);

  const handleApply = () => {
    window.REACT_APP_NAVIGATE('/apply');
  };

  return (
    <section ref={sectionRef} className="relative bg-dark-bg py-20 md:py-32 lg:py-40 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(20, 184, 166, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 184, 166, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Dynamic Background Glow */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="relative bg-gradient-to-br from-dark-card to-dark-light border border-dark-border rounded-2xl md:rounded-3xl p-10 md:p-16 lg:p-20 text-center overflow-hidden"
        >
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 via-transparent to-transparent pointer-events-none"></div>
          
          {/* Content */}
          <div className="relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight"
            >
              Ready to remove your bottleneck?
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-base md:text-lg lg:text-xl text-gray-400 mb-8 md:mb-10 lg:mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              Start with a Growth Bottleneck Audit to identify your biggest constraint
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6"
            >
              <button
                onClick={handleApply}
                className="w-full sm:w-auto bg-accent-blue text-white px-8 md:px-10 lg:px-12 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:bg-blue-600 transition-all duration-300 shadow-lg shadow-accent-blue/20 hover:shadow-accent-blue/40 hover:scale-105 whitespace-nowrap min-h-[56px] flex items-center justify-center cursor-pointer"
              >
                Apply for an Audit
              </button>
              
              <a
                href="mailto:hello@forcem.co"
                className="w-full sm:w-auto border-2 border-dark-border text-white px-8 md:px-10 lg:px-12 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:border-accent-blue hover:bg-accent-blue/5 transition-all duration-300 whitespace-nowrap min-h-[56px] flex items-center justify-center cursor-pointer"
              >
                Contact Us
              </a>
            </motion.div>

            {/* Trust Indicator */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: false, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-xs md:text-sm text-gray-500 mt-8 md:mt-10"
            >
              Trusted by founder-led companies from $1M to $100M in revenue
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesCTA;